import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import type { CameraStore } from '../canvas/CameraStore.js'

/** Luft zum linken und rechten Fensterrand. */
const RAND = 10
/**
 * Was oben und unten belegt ist: Kopfleiste (14 px Abstand + Höhe) und die
 * Einfügen-Leiste über dem Fußbereich. Eine schwebende Leiste, die sich
 * darunter schöbe, wäre zwar im Fenster, aber trotzdem verdeckt.
 */
const OBEN = 62
const UNTEN = 72

/**
 * Hält eine schwebende Leiste oder ein Aufklappmenü im sichtbaren Bereich.
 *
 * Die Bedienleisten auf der Leinwand hängen an ihrem Objekt: die Frame-Leiste
 * unter dem Frame, die Textleiste über dem Text. Liegt der Frame halb unter dem
 * Fensterrand, liegt seine Leiste ganz darunter — bedienbar war sie dann nur
 * noch nach dem Verschieben des ganzen Boards. Dasselbe gilt für Aufklappmenüs
 * dicht am Rand.
 *
 * Statt zu spiegeln („passt unten nicht, also oben") wird geschoben: Die Leiste
 * bleibt so nah an ihrem Objekt wie möglich und rückt nur so weit ins Fenster,
 * wie sie muss. Geschoben wird über die eigenständige `translate`-Eigenschaft,
 * nicht über `transform` — die tragen einige dieser Elemente schon für ihre
 * Zentrierung oder ihre Gegenskalierung, und die soll erhalten bleiben.
 */
/**
 * Wie viele Bildschirmpunkte eine verschobene Einheit zählt.
 *
 * `translate` wirkt im Koordinatensystem des Elternelements — also *nach* allen
 * Skalierungen darüber und *vor* der eigenen. Beides steckt im Verhältnis von
 * gemessener zu gesetzter Breite: Die Kamera zu fragen reichte nicht, denn ein
 * Menü in einer bereits gegengerechneten Leiste steht wieder im Maßstab 1.
 */
function massstabVon(el: HTMLElement, breite: number): number {
  if (el.offsetWidth <= 0) return 1
  const eigene = new DOMMatrixReadOnly(getComputedStyle(el).transform).a
  if (!Number.isFinite(eigene) || eigene <= 0) return 1
  const massstab = breite / el.offsetWidth / eigene
  return Number.isFinite(massstab) && massstab > 0 ? massstab : 1
}

export function useKeepInView<T extends HTMLElement = HTMLDivElement>(
  /** Nötig für alles im mitskalierten Layer: Zoomen löst keinen Render aus. */
  camera?: CameraStore,
): React.RefObject<T | null> {
  const ref = useRef<T | null>(null)
  const versatz = useRef({ x: 0, y: 0 })

  const nachfuehren = useCallback((): void => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return

    const zoom = massstabVon(el, rect.width)

    // Ist das Objekt selbst aus dem Bild gewandert, hat auch seine Leiste dort
    // nichts mehr zu suchen: Sie klebte sonst am Fensterrand und bezöge sich auf
    // etwas, das niemand sieht. Sie fährt dann einfach mit.
    const anker = (el.offsetParent ?? el.parentElement)?.getBoundingClientRect()
    if (
      anker &&
      (anker.right < 0 ||
        anker.left > window.innerWidth ||
        anker.bottom < 0 ||
        anker.top > window.innerHeight)
    ) {
      if (versatz.current.x !== 0 || versatz.current.y !== 0) {
        versatz.current = { x: 0, y: 0 }
        el.style.translate = ''
      }
      return
    }

    // Der bisherige Versatz wird herausgerechnet: Gemessen wird immer die
    // *ungeschobene* Lage, sonst schaukelt sich die Korrektur auf und die
    // Leiste fände nie wieder an ihr Objekt zurück.
    const links = rect.left - versatz.current.x * zoom
    const oben = rect.top - versatz.current.y * zoom

    // Erst nach unten/rechts begrenzen, dann nach oben/links: Passt die Leiste
    // gar nicht ins Fenster, gewinnt die obere linke Ecke.
    const zielX = Math.max(RAND, Math.min(links, window.innerWidth - RAND - rect.width))
    const zielY = Math.max(OBEN, Math.min(oben, window.innerHeight - UNTEN - rect.height))

    // Unter einem halben Bildschirmpunkt lohnt das Schreiben nicht — sonst
    // zappelt die Leiste bei jeder Mausbewegung.
    if (Math.abs(zielX - links - versatz.current.x * zoom) < 0.5 &&
        Math.abs(zielY - oben - versatz.current.y * zoom) < 0.5) {
      return
    }

    const x = (zielX - links) / zoom
    const y = (zielY - oben) / zoom
    versatz.current = { x, y }
    el.style.translate = x === 0 && y === 0 ? '' : `${x}px ${y}px`
  }, [camera])

  // Nach jedem Render: Auswahl gewechselt, Objekt verschoben, Menü geöffnet.
  useLayoutEffect(nachfuehren)

  useEffect(() => {
    // Zoomen und Pannen lösen bewusst keinen Render aus (siehe CameraStore) —
    // deshalb hier ein eigenes Abonnement.
    if (!camera) return
    nachfuehren()
    return camera.subscribe(nachfuehren)
  }, [camera, nachfuehren])

  useEffect(() => {
    // `capture`, damit auch das Scrollen in einem Dialog ankommt.
    window.addEventListener('resize', nachfuehren)
    window.addEventListener('scroll', nachfuehren, true)
    return () => {
      window.removeEventListener('resize', nachfuehren)
      window.removeEventListener('scroll', nachfuehren, true)
    }
  }, [nachfuehren])

  return ref
}
